import type React from 'react'
import { useEffect } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import { canvasMinForSingleShapePanel } from '@/lib/shapes/shape-panel-bounds'
import { toDisplayPosition } from '../helpers/panel-position'
import { rotationForShapeState } from '../helpers/rotation-degrees'
import {
  multiSelectionCanvasUnionAabb,
  multiSelectionPanelWidthHeight,
} from '../helpers/selection-bounds'
import { thicknessSliderTupleIfUnchanged } from '../helpers/slider-tuples'
import {
  selectionFrozenChecked,
  selectionIgnoreTextWrapChecked,
  selectionIgnoreTextWrapMixed,
  selectionLockedChecked,
} from '../helpers/switch-checked-state'
import type {
  DimensionValues,
  FormatForDisplay,
  PanelFieldErrors,
  PositionValues,
} from '../types/panel-fields'

type SetState<T> = React.Dispatch<React.SetStateAction<T>>

/**
 * Mirrors the current selection into every panel control (X/Y/W/H, rotation, appearance, switches)
 * whenever the selection, unit precision or page origin changes. Every setter/callback passed in
 * must be stable — they are effect dependencies.
 */
export function useSelectionSync(params: {
  activeObject: ShapeData | null
  activeObjects: ShapeData[]
  isMultiSelect: boolean
  shapeManagerRef?: React.RefObject<ShapeManagerRef | null>
  useSecondPageOrigin: boolean
  rightPageOriginX: number
  rightPageOriginY: number
  formatForDisplay: FormatForDisplay
  setPosition: SetState<PositionValues>
  setDimensions: SetState<DimensionValues>
  setFieldErrors: SetState<PanelFieldErrors>
  panelRotationSliderActiveRef: React.MutableRefObject<boolean>
  getMixedMultiSelectPanelRotationDeg: () => number
  syncRotationDisplay: (deg: number) => void
  setBorderThickness: SetState<number[]>
  setColors: SetState<{ fill: string; stroke: string }>
  setFreezeObject: SetState<boolean>
  setLockObject: SetState<boolean>
  setIgnoreWrap: SetState<boolean>
  setIgnoreWrapMixed: SetState<boolean>
}): void {
  const {
    activeObject,
    activeObjects,
    isMultiSelect,
    shapeManagerRef,
    useSecondPageOrigin,
    rightPageOriginX,
    rightPageOriginY,
    formatForDisplay,
    setPosition,
    setDimensions,
    setFieldErrors,
    panelRotationSliderActiveRef,
    getMixedMultiSelectPanelRotationDeg,
    syncRotationDisplay,
    setBorderThickness,
    setColors,
    setFreezeObject,
    setLockObject,
    setIgnoreWrap,
    setIgnoreWrapMixed,
  } = params

  useEffect(() => {
    const origin = { useSecondPageOrigin, rightPageOriginX, rightPageOriginY }

    if (isMultiSelect) {
      const mgr = shapeManagerRef?.current
      const union = multiSelectionCanvasUnionAabb(activeObjects)
      const panelWh = multiSelectionPanelWidthHeight({
        shapes: activeObjects,
        shapeManager: mgr ?? null,
      })

      const displayX = toDisplayPosition(union.minX, 'x', origin)
      const displayY = toDisplayPosition(union.minY, 'y', origin)

      setPosition({
        x: formatForDisplay(displayX, 'x'),
        y: formatForDisplay(displayY, 'y'),
      })
      setDimensions({
        width: formatForDisplay(panelWh.width, 'width'),
        height: formatForDisplay(panelWh.height, 'height'),
      })
      if (!panelRotationSliderActiveRef.current) {
        syncRotationDisplay(getMixedMultiSelectPanelRotationDeg())
      }

      setBorderThickness((prev) => thicknessSliderTupleIfUnchanged(prev, 0))
      setColors({ fill: '#f0f0f0', stroke: '#ffffff' })
      setFreezeObject(selectionFrozenChecked(activeObjects))
      setLockObject(selectionLockedChecked(activeObjects))
      setIgnoreWrap(selectionIgnoreTextWrapChecked(activeObjects))
      setIgnoreWrapMixed(selectionIgnoreTextWrapMixed(activeObjects))
      setFieldErrors({})

      return
    }

    if (!activeObject) {
      setLockObject(false)
      setFreezeObject(false)
      setIgnoreWrap(false)
      setIgnoreWrapMixed(false)
      return
    }

    const { minX, minY } = canvasMinForSingleShapePanel(activeObject)
    const displayX = toDisplayPosition(minX, 'x', origin)
    const displayY = toDisplayPosition(minY, 'y', origin)

    setPosition({
      x: formatForDisplay(displayX, 'x'),
      y: formatForDisplay(displayY, 'y'),
    })
    setDimensions({
      width: formatForDisplay(activeObject.width, 'width'),
      height: formatForDisplay(activeObject.height, 'height'),
    })
    syncRotationDisplay(rotationForShapeState(activeObject.rotation ?? 0))
    setBorderThickness((prev) =>
      thicknessSliderTupleIfUnchanged(prev, activeObject.strokeWidth ?? 1)
    )
    setColors({
      fill: activeObject.fill ?? '#e5e7eb',
      stroke: activeObject.stroke ?? '#000000',
    })
    setLockObject(activeObject.locked === true)
    setFreezeObject(activeObject.frozen === true)
    setIgnoreWrap(activeObject.ignoreTextWrap === true)
    setIgnoreWrapMixed(false)
    setFieldErrors({})
  }, [
    activeObject,
    activeObjects,
    getMixedMultiSelectPanelRotationDeg,
    isMultiSelect,
    formatForDisplay,
    useSecondPageOrigin,
    rightPageOriginX,
    rightPageOriginY,
    shapeManagerRef,
    setLockObject,
    setPosition,
    setDimensions,
    setFieldErrors,
    panelRotationSliderActiveRef,
    syncRotationDisplay,
    setBorderThickness,
    setColors,
    setFreezeObject,
    setIgnoreWrap,
    setIgnoreWrapMixed,
  ])
}
